<script setup>
// @ts-check

import { api } from '@/api'
import { getAttachment } from '@/api/attachments'
import BaseActionMenuButton from '@/components/base/menus/BaseActionMenuButton.vue'
import FileCarousel from '@/components/file/FileCarousel.vue'
import ReferenceStatusBadge from '@/components/references/ReferenceStatusBadge.vue'
import ReferenceTitle from '@/components/references/ReferenceTitle.vue'
import { useActions } from '@/core/composables/useActions'
import { ref, toRefs, watchEffect } from 'vue'

const props = defineProps({
  reference: {
    /** @type {import('vue').PropType<ReferenceInterface>} */
    type: Object,
    required: true
  }
})

const { reference } = toRefs(props)

defineOptions({
  name: 'ReferenceDatatablePreview'
})

/** @type {import('vue').Ref<any>} */
const preview = ref(null)

/** @type {import('vue').Ref<?Attachments>} */
const attachments = ref(null)

const hasAttachmentsError = ref(false)

const { mappedActions, triggerAction } = useActions(reference, {
  // eslint-disable-next-line vue/no-ref-object-reactivity-loss
  labelPath: `references.${reference.value.type}.actions`
})

watchEffect(async () => {
  preview.value = null
  attachments.value = null

  const initialId = props.reference.id
  const res = await api.get(props.reference.uri)
  if (initialId !== props.reference.id) {
    return
  }
  preview.value = res.data
})

watchEffect(async () => {
  if (!preview.value?.attachments?.length) {
    return
  }

  hasAttachmentsError.value = false
  try {
    attachments.value = await Promise.all(
      preview.value.attachments.map(getAttachment)
    )
  } catch (error) {
    console.error(error)
    hasAttachmentsError.value = true
  }
})
</script>

<template>
  <div class="divide-y-1 divide-solid divide-secondary-300">
    <!-- ATTACHMENTS -->
    <div
      v-if="preview?.attachments?.length"
      class="flex min-h-28 items-center justify-center py-4"
    >
      <FileCarousel
        v-if="attachments"
        class="w-full"
        :has-error="hasAttachmentsError"
        :carousel-height="320"
        :files="attachments"
      />
      <ProgressCircular v-else />
    </div>

    <!-- MAIN HEADER WITH STATUS BADGE -->
    <div class="flex flex-row items-center justify-between py-4">
      <ReferenceTitle class="heading-4" :title="reference.title" />
      <slot name="badge" :reference>
        <ReferenceStatusBadge :reference />
      </slot>
    </div>

    <!-- AVAILABLE ACTIONS -->
    <div v-if="mappedActions.length" class="divide-y-1 divide-solid">
      <BaseActionMenuButton
        v-for="action in mappedActions"
        :key="action.action"
        :icon="action.icon"
        class="px-0! py-4!"
        :label="action.label"
        @click.stop="triggerAction(action, $emit)"
      />
    </div>

    <!-- CUSTOM CONTENT -->
    <slot name="preview" :preview :reference />
  </div>
</template>
